import { useEffect, useState } from "react";
import {
  Card,
  Select,
  DatePicker,
  Button,
  Spin,
  Modal,
  message
} from "antd";
import {
  collection,
  getDocs,
  addDoc,
  query,
  where,
  Timestamp
} from "firebase/firestore";
import { db } from "../../config/firebase.js";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeftOutlined } from "@ant-design/icons";

const { RangePicker } = DatePicker;

export default function GenerateInvoices() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [type, setType] = useState(null);
  const [datas, setDatas] = useState([]);
  const [selectedDatas, setSelectedDatas] = useState([]);
  const [dateRange, setDateRange] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const urlType = searchParams.get("type");

    if (urlType === "company" || urlType === "agent") {
      setType(urlType);
    } else {
      setType("company");
    }
  }, [searchParams]);

  
  useEffect(() => {
    const loadDatas = async (type) => {
      const snap = await getDocs(collection(db, type === 'company' ? "clients": "drivers"));
      const list = snap.docs.map(doc => ({
        id: doc.id,
        name: type === "company" ? doc.data().enterprise : `${doc.data().firstName} ${doc.data().lastName}`
      }));
      setDatas(list);
    };
    if (type) {
      loadDatas(type);
    }
  }, [type]);
  
  const generateInvoices = async () => {
    if (!selectedDatas.length || !dateRange) {
      Modal.warning({
        title: "Informations manquantes",
        content: "Veuillez sélectionner au moins une entreprise et une période."
      });
      return;
    }

    setLoading(true);
    
    try {
      const [from, to] = dateRange;
      
      for (const dataId of selectedDatas) {
        const data = datas.find(c => c.id === dataId);
        
        const ticketsQ = query(
          collection(db, "tickets"),
          where(type === "company" ? "client.uid": "driver.id", "==", dataId),
          where("createdAt", ">=", Timestamp.fromDate(from.toDate())),
          where("createdAt", "<=", Timestamp.fromDate(to.toDate())),
        );

        const ticketsSnap = await getDocs(ticketsQ);

        let totalAmount = 0;
        let ticketsCount = 0;
        let missingTickets = 0;

        ticketsSnap.forEach(doc => {
          const t = doc.data();
          if (t.rateApplied?.[`${type}Rate`]) {
            totalAmount += Number(t.rateApplied?.[`${type}Rate`]?.amount);
            ticketsCount++;
          } else {
            missingTickets++
          }
        });

        if (missingTickets > 0) {
          Modal.error({
            title: "Facturation impossible",
            content: (
              <>
                <p>
                  {missingTickets} ticket(s) n'ont pas de tarif appliqué pour les dates sélétionnées.
                </p>
                <p>
                  Veuillez appliquer les tarifs avant de générer la facture.
                </p>
              </>
            ),
            okText: "Compris"
          });
          setLoading(false);
          return;
        }

        await addDoc(collection(db, "invoices"), {
          number: `FAC-${new Date().getFullYear()}-${Date.now()}`,
          type,
          [`${type}-Id`]: dataId,
          name: data.name,
          period: {
            from: Timestamp.fromDate(from.toDate()),
            to: Timestamp.fromDate(to.toDate())
          },
          totalAmount,
          ticketsCount,
          status: "final",
          createdAt: Timestamp.now()
        });
      }

      message.success("Factures générées avec succès ✅");
      setSelectedDatas([]);
      setDateRange(null);

    } catch (error) {
      console.error(error);
      Modal.error({
        title: "Erreur",
        content: "Une erreur est survenue lors de la génération des factures."
      });
    } finally {
      setLoading(false);
    }
  };

  if (!type) return null;

  return (
    <Spin spinning={loading} tip="Génération des factures...">
      <Card
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <ArrowLeftOutlined
              style={{ fontSize: 18, cursor: "pointer" }}
              onClick={() => navigate("/admin/invoices")}
            />
            <span>Générer des factures</span>
          </div>
        }
        style={{ maxWidth: 600, margin: "0 auto" }}
      >
        <div style={{ marginBottom: 16 }}>
          {type ==="company" ? <label>Entreprises</label> : <label>Agents</label>}
          <Select
            mode="multiple"
            style={{ width: "100%" }}
            placeholder={type ==="company" ? "Sélectionner les entreprises" : "Sélectionner les agents"}
            value={selectedDatas}
            onChange={setSelectedDatas}
            options={datas.map(c => ({
              value: c.id,
              label: c.name
            }))}
          />
        </div>

        <div style={{ marginBottom: 24 }}>
          <label>Période</label>
          <RangePicker
            style={{ width: "100%" }}
            value={dateRange}
            onChange={setDateRange}
          />
        </div>

        <Button
          type="primary"
          block
          size="large"
          onClick={generateInvoices}
          disabled={loading}
        >
          Générer les factures
        </Button>
      </Card>
    </Spin>
  );
}
